import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { MATERIALS } from "@/lib/site";
import { Eyebrow, Reveal, EASE } from "./ui";
import { MaterialDialog } from "./MaterialDialog";

const FILTERS = [
  { id: "tutti", label: "Të gjitha" },
  { id: "mermer", label: "Mermer" },
  { id: "granit", label: "Granit" },
];

const Card = ({ m, i, onOpen }) => (
  <motion.button
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: EASE }}
    onClick={() => onOpen(m)}
    data-testid={`material-card-${m.id}`}
    className="group text-left"
  >
    <div className="slab-frame relative aspect-[3/4] overflow-hidden bg-[#16161A]">
      <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover scale-[1.08] transition-transform duration-[1400ms] ease-out group-hover:scale-100" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />
      <span className="absolute top-4 left-4 font-mono-x text-[11px] tracking-[0.25em] uppercase text-white bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">{m.type}</span>
      <span className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#C9A55C] text-[#0B0B0C] grid place-items-center scale-75 opacity-0 transition duration-500 group-hover:scale-100 group-hover:opacity-100">
        <Plus className="w-4 h-4" />
      </span>
    </div>
    <div className="pt-5 flex items-baseline justify-between gap-3 border-b border-white/10 pb-4">
      <h3 className="font-display text-2xl sm:text-3xl text-[#F3EEE4] transition-colors group-hover:text-[#E3C88A]">{m.name}</h3>
      <span className="font-mono-x text-[11px] tracking-[0.2em] uppercase text-[#A8A196] text-right">{m.finish}</span>
    </div>
  </motion.button>
);

export const Catalog = () => {
  const [filter, setFilter] = useState("tutti");
  const [active, setActive] = useState(null);
  const list = MATERIALS.filter((m) => filter === "tutti" || m.type === filter);

  return (
    <section id="materiali" className="relative bg-[#0B0B0C] py-24 sm:py-36" data-testid="catalog-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14 sm:mb-20">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Katalogu i materialeve</Eyebrow>
            <h2 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl text-[#F3EEE4] mt-6 leading-[1]">
              Çdo pllakë <em className="text-gold-grad">ka një histori</em>.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="text-[#CFC8BA] text-base md:text-lg leading-relaxed">
              Një përzgjedhje mermeresh dhe granitesh në pllaka. Prekni një gur për të zbuluar përfundimet dhe përdorimet, dhe na pyesni për disponueshmërinë.
            </p>
            <div className="mt-8 inline-flex p-1 rounded-full border border-white/15" role="tablist">
              {FILTERS.map((f) => (
                <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`catalog-filter-${f.id}`} className={`relative px-5 sm:px-6 py-2.5 text-sm rounded-full transition-colors ${filter === f.id ? "text-[#0B0B0C]" : "text-[#D9D3C7] hover:text-white"}`}>
                  {filter === f.id && <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full bg-[#C9A55C]" transition={{ duration: 0.5, ease: EASE }} />}
                  <span className="relative">{f.label}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="catalog-grid">
          <AnimatePresence>
            {list.map((m, i) => <Card key={m.id} m={m} i={i} onOpen={setActive} />)}
          </AnimatePresence>
        </motion.div>
      </div>
      <MaterialDialog material={active} onClose={() => setActive(null)} />
    </section>
  );
};
